<template>
  <div class="tweet-detail">
    <processing v-if="isProcessing"></processing>
    <div class="img-box" ref="img-box" :style="{
        width: imgBoxWidth + '%',
        height: imgBoxHeight + '%',
        backgroundColor: imgBgColor
    }">
      <div class="img-cont">
        <round-button v-if="!isStocked" @click="() => {addToStock()}" src="star_off"> </round-button>
        <round-button v-if="isStocked" @click="removeFromStock" src="star_on"> </round-button>
      </div>

      <div class="img" :style="{
        backgroundImage: isLoaded ? `url('${imgsrc}'), url('${altsrc}') ` : `url('${altsrc}')`,
        width: imgWidth + '%',
        height: imgHeight + '%',
        marginTop: imgMarginTop + '%',
        filter: `blur(${isLoaded ? 0 : 10}px)`,
        transform: `scale(${isLoaded ? 1 : 1.1})`
      }" @click="toggleImgView">
      </div>
      <tweet-text class="tweet-text" v-if="tweet && !isImgFullView"
        :userIconSrc="tweet.user.profile_image_url_https"
        :userName="tweet.user.name"
        :text="tweet.text">
      </tweet-text>
    </div>
    <div class="cols" :style="{
      height: `calc(${colHeight}% - 0px)`
      }"
      @click="toggleColorList"
    >
      <loading-anim class="loading-anim" v-show="!isLoaded"></loading-anim>
      <div class="colorset-cont">
        <round-button v-if="isLoaded && !stock" src="plus" @click="colorCountChange(1)"> </round-button>
        <round-button v-if="isLoaded && !stock" src="minus" @click="colorCountChange(-1)"></round-button>
      </div>
      <colorset-view :colorset="colorset || blankColorset" :name="isLoaded ? 'cs' : 'alt'" @color-change="colorCountChange" :style="{
        filter: `blur(${isLoaded ? 0 : 30}px)`,
        transform: `scale(${isLoaded ? 1 : 1.5})`,
      }"></colorset-view>
    </div>
    <colorset-list-view class="color-list"
      v-if="isLoaded && isColorListView && colorset"
      :colorset="colorset"
      @click="toggleColorList">
    </colorset-list-view>
    <stock-share-btn class="share"
      v-if="isShareAllowed"
      @click="tweetStock"
    ></stock-share-btn>
  </div>
</template>

<style scoped>
.tweet-detail{
  height: 100%;
}
.img-box {
  width: 100%;
  height: 50%;
  display: block;
  position: relative;
  overflow: hidden;
  transition:
    width 300ms 0.15s cubic-bezier(.41,.8,.67,1),
    height 300ms 0.15s cubic-bezier(.41,.8,.67,1),
    background-color 300ms 0.15s ease-out;
}
.img {
  width: 100%;
  height: 100%;
  display: inline-block;
  background-size: cover;
  background-position: center;
  text-align: center;
  margin: auto;
  transition:
    width 250ms 0s cubic-bezier(.41,.8,.67,1),
    height 250ms 0s cubic-bezier(.41,.8,.67,1),
    margin 250ms 0s cubic-bezier(.41,.8,.67,1),
    transform 500ms 0s ease-out,
    filter 500ms 0s linear;
}
.img-cont {
  position: absolute;
  z-index: 1;
  right: 10px;
  top: 20px;
}
.cols {
  width: 100%;
  height: 50%;
  position: relative;
  transition:
    filter 350ms 0s linear,
    transform 350ms 0s linear;
}
.tweet-text{
  position: absolute;
  bottom: 0;
  width: 100%;
}
.loading-anim {
  bottom: calc(50% - 10px);
  position: absolute;
  z-index: 1;
}
.colorset-cont {
  position: absolute;
  z-index: 2;
  left: 10px;
  top: calc(50% - 35px);
}
.color-list {
  position: absolute;
  z-index: 3;
  bottom: 0;
}
.round-button{
  margin-bottom: 10px;
}
.composer {
  width: 100%;
  height: 100%;
  position: absolute;
  bottom: 0;
}
.share {
  width: 100%;
  height: 45px;
  position: absolute;
  bottom: 0;
}
</style>

<script>
import ColorAnalyzerApi from '@/api/ColorAnalyzerApi'
import UserApi from '../api/UserApi'
import StockApi from '../api/StockApi'
import DynamicLinkApi from '../api/DynamicLinkApi'
import Colorset from '@/models/Colorset'
import StockItem from '@/models/StockItem'
import ColorsetView from '@/components/ColorsetView'
import RoundButton from '@/components/RoundButton'
import LoadingAnim from '@/components/LoadingAnim'
import Processing from '@/components/Processing'
import TweetText from '@/components/TweetText'
import ColorsetListView from '@/components/ColorsetListView'
import StockShareBtn from '@/components/StockShareBtn'
import getImageDataFromUrl from '@/logic/getImageDataFromUrl'

const COLOR_MAX = 10
const COLOR_MIN = 3

export default {
  name: 'TweetDetail',
  components: {ColorsetView, RoundButton, LoadingAnim, Processing, TweetText, ColorsetListView, StockShareBtn},
  props: {
    imgsrc: String,
    altsrc: String,
    tweet: Object,
    mindex: Number,
    stock: StockItem
  },
  data () {
    return {
      analyzer: null,
      colorCount: 5,
      isImgFullView: false,
      altColorset: null,
      blankColorset: new Colorset(),
      isPending: true,
      isProcessing: false,
      isColorListView: false,
      shareBtnHeight: 45,
      elementHeight: document.documentElement.clientHeight
    }
  },
  computed: {
    colorset () {
      if (this.stock && this.stock.colorset) {
        return this.stock.colorset
      }
      if (!this.isLoaded) {
        return this.altColorset
      }
      const cs = this.$data.analyzer.clustering(this.colorCount)
      return cs
    },
    isLoaded () {
      return this.$data.analyzer != null && !this.$data.isPending
    },
    isStocked () {
      return this.stock && this.stock.uid === UserApi.uid
    },
    imgBgColor () {
      return '#000'
    },
    imgBoxHeight () {
      const w = document.documentElement.clientWidth
      const h = document.documentElement.clientHeight
      return Math.min(w / h * 100, 67)
    },
    imgHeight () {
      const isFull = this.$data.isImgFullView
      if (!isFull || !this.analyzer) {
        return 100
      } else {
        const iw = this.analyzer.imgWidth
        const ih = this.analyzer.imgHeight
        const box = this.$refs['img-box']
        const bw = box.clientWidth
        const bh = box.clientHeight
        if (iw / ih <= bw / bh) {
          return 100
        }
        return (ih / iw) * (bw / bh) * 100
      }
    },
    imgMarginTop () {
      return (100 - this.imgHeight) / 2
    },
    imgBoxWidth () {
      return '100%'
    },
    imgWidth () {
      const isFull = this.$data.isImgFullView
      if (!isFull || !this.analyzer) {
        return 100
      } else {
        const iw = this.analyzer.imgWidth
        const ih = this.analyzer.imgHeight
        const box = this.$refs['img-box']
        const bw = box.clientWidth
        const bh = box.clientHeight
        if (iw / ih > bw / bh) {
          return 100
        }
        return (iw / ih) * (bh / bw) * 100
      }
    },
    colHeight () {
      return 100 - this.imgBoxHeight
    },
    isSaveAllowed () {
      const owner = this.tweet ? this.tweet.user.id_str : null
      return owner && owner === UserApi.twUid
    },
    isShareAllowed () {
      return this.isSaveAllowed
    }
  },
  methods: {
    async imgChanged (newsrc) {
      this.$data.analyzer = null
      if (!newsrc) { return }
      this.$data.analyzer = await ColorAnalyzerApi.analyzeFromUrl(newsrc)
    },
    colorCountChange (dCount) {
      this.$data.colorCount = Math.max(COLOR_MIN, Math.min(COLOR_MAX, this.$data.colorCount + dCount))
    },
    toggleImgView () {
      this.$data.isImgFullView = !this.$data.isImgFullView
    },
    addToStock (callback) {
      const tw = this.tweet
      const mindex = this.mindex
      const stock = new StockItem(tw.id_str, mindex, this.imgsrc, this.colorset)
      // set if tweet private
      stock.isPrivateSource = !(tw.user.protected === false)
      stock.isOwnSource = this.tweet.user.id_str === UserApi.twUid
      StockApi.addToStocks(stock).then((st) => {
        // this.stock = st
        if (callback) {
          callback(st)
        }
        this.$emit('stocked', st)
        this.$data.isProcessing = false
      })
      this.$data.isProcessing = true
    },
    removeFromStock () {
      StockApi.removeFromStocks(this.stock.sid).then(() => {
        // this.stock = null
        this.$emit('unstocked', this.stock)
        this.$data.isProcessing = false
      })
      this.$data.isProcessing = true
    },
    tweetStock () {
      const openTweetPopup = (stock) => {
        const text = encodeURIComponent('カラーインコでカラーパレットを作ったよ')
        const pageUrl = `${document.location.protocol}//${document.location.host}/stock/${stock.sid}`
        const pop = window.open('', 'colorincotweet')
        pop.document.body.innerHTML = 'loading...'
        DynamicLinkApi.shorten(pageUrl).then(shortUrl => {
          const twurl = `https://twitter.com/share?text=${text}&url=${shortUrl || pageUrl}&hashtags=colorinco`
          window.open(twurl, 'colorincotweet')
        })
        // call opg image func to cache
        const ogpImgUrl = `${document.location.protocol}//${document.location.host}/ogp/stockimg/${stock.sid}`
        getImageDataFromUrl(ogpImgUrl)
      }
      this.addToStock()
      const dummyStock = new StockItem(this.tweet.id_str, this.mindex)
      dummyStock.uid = UserApi.uid
      openTweetPopup(dummyStock)
    },
    closeTweetDialog () {
      this.tweetDialogSrc = null
    },
    toggleColorList () {
      this.isColorListView = !this.isColorListView
    },
    isInStockList () {
      const uid = UserApi.uid
      const statusid = this.tweet ? this.tweet.id_str : ''
      const mindex = this.mindex
      const sid = this.Stock ? this.stock.sid : `${uid}:${statusid}:${mindex}`
      return StockApi.isInCache(sid)
    }
  },
  watch: {
    imgsrc (newVal) {
      this.imgChanged(newVal)
    },
    altsrc (newVal) {
      if (!newVal) { return }
      ColorAnalyzerApi.colorsetFromUrl(newVal, 5).then((cs) => {
        this.altColorset = cs
      }).catch(err => {
        console.error(this.altsrc, err)
      })
    }
  },
  mounted () {
    this.imgChanged(this.imgsrc)
    window.setTimeout(() => {
      this.$data.isPending = false
    }, 400)
  }
}
</script>



// WEBPACK FOOTER //
// src/components/TweetDetail.vue